import noop from 'lodash/noop';
import { ReactElement } from 'react';

import { Icd10Code } from '@dreryk/egabinet-icd-dictionary-published-language-api';
import {
   VisitDiagnosisMarkAsChronicCommand,
   VisitDiagnosisMarkAsNonChronicCommand,
} from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { ActionEventInterceptor } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { ChronicDiseaseRemovalParameters } from '../removalChronicDisease/RemovalChronicDiseaseModal.types';

import { useRetryActionAfterChronicDiseaseCannotBeRemovedWithoutReasonFault } from './shared/useRetryActionAfterChronicDiseaseCannotBeRemovedWithoutReasonFault';
import { VisitDiagnosisCommonFault } from './shared/useVisitDiagnosisFaultHandlerMap';

type VisitDiagnosisChronicEventInterceptorProps = {
   readonly children: ReactElement;
   readonly visitId: number;
   readonly code: string;
   readonly chronic: boolean;
   readonly disabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const VisitDiagnosisChronicEventInterceptor = (
   props: VisitDiagnosisChronicEventInterceptorProps
): JSX.Element => {
   const { executeAsync: markAsChronic } = useCqrsCommand<
      void,
      ChronicDiseaseRemovalParameters | void,
      VisitDiagnosisCommonFault
   >(
      () => {
         const command = new VisitDiagnosisMarkAsChronicCommand();
         command.visitId = props.visitId;
         command.code = Icd10Code.of(props.code);
         return command;
      },
      {
         onUnexpectedError: noop,
      }
   );

   const { executeAsync: markAsNonChronic } = useCqrsCommand<
      void,
      ChronicDiseaseRemovalParameters | void,
      VisitDiagnosisCommonFault
   >(
      (parameters) => {
         const command = new VisitDiagnosisMarkAsNonChronicCommand();
         command.visitId = props.visitId;
         command.code = Icd10Code.of(props.code);
         if (parameters) {
            command.removalDate = Choice.of(parameters.chronicDiseaseRemovalDate!);
            command.removalReason = Choice.of(parameters.chronicDiseaseRemovalReason!);
         }
         return command;
      },
      {
         onUnexpectedError: noop,
      }
   );

   const action = useRetryActionAfterChronicDiseaseCannotBeRemovedWithoutReasonFault(async (parameters) => {
      if (props.disabled) {
         return noop();
      }

      if (props.chronic) {
         return markAsNonChronic(parameters);
      }

      return markAsChronic(parameters);
   });

   return <ActionEventInterceptor action={action}>{props.children}</ActionEventInterceptor>;
};
